import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Brain, Box, Mail, ArrowLeft, Check } from 'lucide-react';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email) {
      setError('Please enter your email address.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/password-reset-request', { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-dark flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-3">
            <div className="relative">
              <Brain className="w-8 h-8 text-primary" />
              <Box className="w-4 h-4 text-accent absolute -bottom-1 -right-1" />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              PrintMind AI
            </span>
          </Link>
        </div>

        <div className="bg-bg-light rounded-xl border border-text-muted/20 p-8" data-testid="forgot-password-form">
          {sent ? (
            /* Success state */
            <div className="text-center space-y-4">
              <div className="w-14 h-14 bg-green-500/15 rounded-full flex items-center justify-center mx-auto">
                <Check className="w-7 h-7 text-green-400" />
              </div>
              <h2 className="text-xl font-bold text-text-primary">Check Your Email</h2>
              <p className="text-sm text-text-secondary leading-relaxed">
                If an account with <span className="text-text-primary font-medium">{email}</span> exists,
                we've sent a password reset link. Please check your inbox and spam folder.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition text-sm"
                data-testid="back-to-login-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </Link>
            </div>
          ) : (
            /* Form state */
            <>
              <h2 className="text-xl font-bold text-text-primary mb-2">Forgot Password?</h2>
              <p className="text-sm text-text-secondary mb-6">
                Enter your email address and we'll send you a link to reset your password.
              </p>

              {error && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm" data-testid="forgot-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-text-secondary block mb-1.5">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full pl-10 pr-4 py-2.5 bg-bg-dark border border-text-muted/20 text-text-primary rounded-lg text-sm focus:outline-none focus:border-primary/50"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition disabled:opacity-50 text-sm"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  to="/login"
                  className="text-sm text-text-muted hover:text-text-primary transition flex items-center justify-center gap-1"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back to Login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
